// frontend/src/components/FileList/FileList.js
// Purpose: Display uploaded files with delete option

// Desired outcome: Show a user-friendly list of uploaded files with metadata
// and options to delete files or select them for analysis

import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { deleteFile, fetchUploadedFiles } from '../../state/analysis/analysisSlice';
import logger from '../../utils/logger';
import './FileList.css';

const FileList = ({ files, isLoading, onDeleteSuccess, onDeleteError }) => {
  const dispatch = useDispatch();
  const [deletingFileId, setDeletingFileId] = useState(null);
  
  // Function to format date in a readable way
  const formatDate = (dateString) => {
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };
  
  // Handle file deletion
  const handleDelete = async (fileID) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      setDeletingFileId(fileID);
      
      try {
        await dispatch(deleteFile(fileID)).unwrap();
        await dispatch(fetchUploadedFiles()).unwrap();
        onDeleteSuccess();
        logger.info(`File deleted: ${fileID}`);
      } catch (error) {
        onDeleteError(error.message || 'Error deleting file');
        logger.error(`Error deleting file: ${fileID}`, error);
      } finally {
        setDeletingFileId(null);
      }
    }
  };

  // Rendering states
  if (isLoading) {
    return <div className="loading">Loading files...</div>;
  }
  
  if (!files || files.length === 0) {
    return <div className="no-files">No files uploaded yet.</div>;
  }

  return (
    <div className="file-list">
      <table>
        <thead>
          <tr>
            <th>File Name</th>
            <th>Upload Date</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {files.map((file) => (
            <tr key={file.fileID}>
              <td>{file.originalName}</td>
              <td>{formatDate(file.uploadDate)}</td>
              <td>
                <button
                  className="delete-button"
                  onClick={() => handleDelete(file.fileID)}
                  disabled={deletingFileId === file.fileID}
                >
                  {deletingFileId === file.fileID ? 'Deleting...' : 'Delete'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default FileList;